/**
 * KioskActivation — UI for entering kiosk mode from SMS admin (ADR-073 Decision 5).
 *
 * Staff selects a building and activates kiosk mode. This is accessed from
 * the Admin page. On activation, the app transitions to the kiosk check-in UI.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import { Button as ButtonComponent, SelectInput } from '@themes/aegix';
import { useBuildings } from '../../hooks/queries';
import { useKiosk } from '../../contexts/KioskContext';
import { useNavigate } from 'react-router-dom';
import { toaster } from '../ui/toaster';

export function KioskActivation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enterKioskMode } = useKiosk();
  const { data: buildingsData, isLoading } = useBuildings({});
  const [selectedBuildingId, setSelectedBuildingId] = useState('');

  const buildings = buildingsData?.results ?? buildingsData ?? [];

  const buildingOptions = Array.isArray(buildings)
    ? buildings.map((b) => ({
        label: b.name,
        value: String(b.id),
      }))
    : [];

  const handleActivate = () => {
    if (!selectedBuildingId) return;

    const building = Array.isArray(buildings)
      ? buildings.find((b) => b.id === Number(selectedBuildingId))
      : null;
    if (!building) return;

    enterKioskMode({
      buildingId: building.id,
      buildingName: building.name,
      schoolId: Number(building.school) || 0,
      schoolName: '',
    });

    toaster.create({
      title: t('kiosk.modeActivatedTitle', 'Kiosk mode activated'),
      description: t('kiosk.modeActivatedDesc', 'Kiosk bound to {{name}}', { name: building.name }),
      type: 'success',
    });

    navigate('/kiosk');
  };

  return (
    <Box maxW="500px" mx="auto" py={8}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading as="h2" size="md" mb={2}>
            {t('kiosk.title', 'Kiosk Mode')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {t('kiosk.activationInstructions', 'Activate kiosk mode to turn this device into a visitor check-in terminal. The app will lock into a visitor-facing check-in interface. Staff credentials are required to exit kiosk mode.')}
          </Text>
        </Box>

        <SelectInput
          label={t('kiosk.selectBuilding', 'Select Building')}
          name="building"
          value={selectedBuildingId}
          onChange={(val: string) => setSelectedBuildingId(val)}
          option={buildingOptions}
          placeholder={isLoading ? t('kiosk.loadingBuildings', 'Loading buildings...') : t('kiosk.chooseBuilding', 'Choose a building')}
          disabled={isLoading}
          aria-label={t('kiosk.selectBuildingAria', 'Select building for kiosk mode')}
          e2eTitle="kiosk-building-select"
        />

        <ButtonComponent
          visual="primary"
          onClick={handleActivate}
          disabled={!selectedBuildingId}
          minH="44px"
          minW="44px"
          data-e2e="activate-kiosk-button"
        >
          {t('kiosk.activateButton', 'Activate Kiosk Mode')}
        </ButtonComponent>
      </VStack>
    </Box>
  );
}
